<template>
  <BlocksCard class="blocks-agent-card">
    <!-- Heading -->
    <h2 class="__heading __underlined">
      {{ agents.length > 0 ? 'Agent' : 'Builder' }}
    </h2>

    <!-- Builder Logo -->
    <nuxt-link v-if="company" :to="`/builder/${company.slug}/view`">
      <img v-lazy-load  class="__builder-logo" :data-src="company.logoUrl" alt="Builder logo" width="68" height="68">
    </nuxt-link>

    <!-- Agent Slider List -->
    <BlocksSlider rotate-slides>
      <div v-for="(agent, index) in agents" :key="`agent-${index}`" class="__inner-wrapper">
        <div class="__agent-content">
          <!-- Agent Avatar -->
          <div class="__agent-avatar">
            <img v-lazy-load  :data-src="agent.user.avatarUrl || '/images/avatars/blank.webp'" :alt="`${agent.displayName}'s profile image`" width="85" height="85">
          </div>

          <!-- Agent Information -->
          <div class="__agent-info">
            <h3 class="__agent-name">
              {{ agent.displayName }}
            </h3>
            <span class="__agent-office">
              {{ agent.office }}
            </span>

            <div class="__agent-contact-details">
              <ElementsButtonsRegular v-for="(phone, key) in agent.phoneNumbers.data" :key="`agent-phone-${key}`" text size="md" :label="phone.toString()" :button-link="`tel:${phone}`" :append-icon="phoneIcon" :analytics-click-event-data="{entity: 'Listing', property: 'call', buttonAction: `call agent: ${agent.displayName}`, displayedLocation: 'listing view'}" @click="triggerPixelLeadEvent('phone')" />
              <ElementsButtonsRegular v-if="location && locationDid" text size="md" label="Email Agent" :append-icon="emailIcon" @click="isEnquiryOpen = true" />
              <ElementsButtonsRegular v-if="location && locationDid" text size="md" label="Chat with Agent" :append-icon="messageIcon" :loading="!!isChatInitiating" @click="triggerInitiateChat(agent.did)" />
            </div>
          </div>
        </div>
      </div>
    </BlocksSlider>

    <!-- If no agents, show builder contact details -->
    <div v-if="!agents.length" class="d-flex flex-column" style="margin: 12px 0;">
      <ElementsButtonsRegular v-for="(phone, key) in officeOrCompanyPhoneNumbers" :key="`company-phone-${key}`" text size="md" :label="phone.toString()" :button-link="`tel:${phone}`" :append-icon="phoneIcon" :analytics-click-event-data="{entity: 'Listing', property: 'call', buttonAction: `call agent: ${company.displayName}`, displayedLocation: 'listing view'}" @click="triggerPixelLeadEvent('phone')" />
      <ElementsButtonsRegular v-if="location && locationDid" text size="md" label="Email Builder" :append-icon="emailIcon" @click="isEnquiryOpen = true" />
      <ElementsButtonsRegular v-if="location && locationDid" text size="md" label="Chat with Builder" :append-icon="messageIcon" :loading="!!isChatInitiating" @click="triggerInitiateChat" />
    </div>

    <!-- Action buttons -->
    <div class="__action-button">
      <ElementsButtonsRegular secondary label="View Listings" size="sm" :button-link="`/builder/${company.slug}/listings`" :analytics-click-event-data="{entity: 'Listing', property: 'button', buttonAction: `view ${company.slug} listings`, displayedLocation: 'listing view'}" />
      <ElementsButtonsRegular secondary label="View Profile" size="sm" :button-link="`/builder/${company.slug}/view`" :analytics-click-event-data="{entity: 'Listing', property: 'button', buttonAction: `view ${company.slug} profile`, displayedLocation: 'listing view'}" />
    </div>

    <!-- Email Enquiry -->
    <CommonEmailEnquiry v-model:is-open="isEnquiryOpen" :enquiry-type="location" :enquiring-entity-id="locationDid" :tracking-event-name="propertyNameData" />
  </BlocksCard>
</template>

<script setup lang="ts">
import phoneIcon from '~/assets/images/icons/phone.webp';
import emailIcon from '~/assets/images/icons/email.png';
import messageIcon from '~/assets/images/icons/message.webp';
import {PropType} from 'vue';
import {Agent} from '~/@types/builder';
import {CompanyWidget, OfficeWidget} from '~/@types/company';
import {useAuthUserStore} from '~/store/authUser';
import {useLayoutStore} from '~/store/layout';
import {Listing} from '~/@types/listing';

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  listing: {
    type: [Object, null] as PropType<Listing>,
    required: false,
    default: null,
  },
  location: {
    type: [String, null] as PropType<string | null>,
    default: null,
  },
  locationDid: {
    type: [String, null] as PropType<string | null>,
    default: null,
  },
  agents: {
    type: Array as PropType<Agent[]>,
    default: () => [],
  },
  company: {
    type: Object as PropType<CompanyWidget>,
    required: true,
  },
  office: {
    type: Object as PropType<OfficeWidget>,
    default: null,
  },
  propertyName: {
    type: String,
    default: '',
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const isChatInitiating: Ref<boolean> = ref(false);
const {$initiateChat, $metaPixelTrackEvent} = useNuxtApp();
const authUserStore = useAuthUserStore();
const router = useRouter();
const layoutStore = useLayoutStore();
const isEnquiryOpen: Ref<boolean> = ref(false);
const propertyNameData: Ref<string> = ref('');

onMounted(() => {
  propertyNameData.value = props.listing?.name ?? props.propertyName;
});

/**
 * Trigger initiate chat
 */
async function triggerInitiateChat (agentDid?: string) {
  if (!props.location || !props.locationDid) return;
  triggerPixelLeadEvent('chat');
  isChatInitiating.value = true;

  authUserStore.setChatInitiationData({
    location: props.location,
    locationDid: props.locationDid,
    agentDid: agentDid ?? null,
    url: window.location.href,
    roomId: null,
  });

  if (authUserStore.user) {
    const response = await $initiateChat(props.location, props.locationDid, agentDid);
    if (response) {
      authUserStore.setChatInitiationData({
        location: props.location,
        locationDid: props.locationDid,
        url: window.location.href,
        roomId: response.code === 200 ? response.data.toString() : null,
        hasError: response.code !== 200,
      });
    }

    setTimeout(() => {
      layoutStore.setChatOpen(true);
      isChatInitiating.value = false;
    }, 1500);
  } else {
    router.push('/auth/login');
    isChatInitiating.value = false;
  }
}

/**
 * Trigger pixel event
 */
function triggerPixelLeadEvent (type: 'phone' | 'chat') {
  const eventProperties = {
    content_name: '',
    content_category: '',
    content_ids: [],
    content_type: '',
    lead_type: type,
  };

  if (props.listing) {
    eventProperties.content_name = props.listing.name;
    eventProperties.content_category = props.listing.listingTypes.data.join(',');
    eventProperties.content_ids = [props.listing.did];
    eventProperties.content_type = 'Listing';
  } else if (props.propertyName) {
    eventProperties.content_name = props.propertyName;
    eventProperties.content_category = 'Design';
    eventProperties.content_ids = [props.locationDid];
    eventProperties.content_type = 'Design';
  } else {
    return;
  }
  // Track meta event
  $metaPixelTrackEvent('Lead', eventProperties);
}

/**
 * Get office or company phone numbers
 */
const officeOrCompanyPhoneNumbers: ComputedRef<string[]> = computed(() => {
  if (props.office) {
    return props.office.phoneNumbers.data;
  } else {
    return props.company.phoneNumbers.data;
  }
});
</script>

<style lang="scss">
.blocks-agent-card {
  position: relative;

  .__heading {
    padding-bottom: 20px;
  }

  .__builder-logo {
    position: absolute;
    right: 20px;
    top: 12px;
    max-width: 68px;
    max-height: 48px;
    width: auto;
    height: auto;
  }

  .__inner-wrapper {
    margin-top: 12px;

    .__agent-content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;

      .__agent-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 85px;
        height: 85px;
        border-radius: 50%;
        overflow: hidden;
        margin-right: 20px;

        img {
          max-width: 100%;
          max-height: 100%;
        }
      }

      .__agent-info {
        padding: 12px;

        .__agent-name {
          font-weight: 400;
          color: rgb(var(--text-default));
        }

        .__agent-office {
          font-size: 14px;
          margin-top: 5px;
          display: block;
          color: rgba(var(--text-default), 0.5);
        }

        .__agent-contact-details {
          margin-top: 12px;

          .el-button-regular {
            display: block;
            padding: 4px;

            .__button-inner {
              min-width: 100px;
            }
          }
        }
      }
    }
  }
}
</style>
